/**
 * Bidirectional mapping between activeTab values and URL paths.
 *
 * During migration the legacy switch(activeTab) still renders screens;
 * this map just keeps the URL bar in sync so every screen is bookmarkable
 * and browser back/forward works.
 */

const TAB_TO_PATH = {
  // Main tabs
  "services":             "/",
  "profile":              "/profile",
  "messages":             "/messages",
  "notifications":        "/notifications",

  // Browse / discovery
  "skills-needed":        "/skills-needed",
  "sponsored-listings":   "/sponsored-listings",
  "community-calendar":   "/calendar",
  "community-chat":       "/chat",

  // Profile editing
  "personal-profile":     "/profile/edit/personal",
  "trading-profile":      "/profile/edit/trading",
  "open-to":              "/profile/edit/open-to",
  "bartering-guide":      "/bartering-guide",

  // Listings / items
  "item-details":         "/listing",
  "my-listings":          "/my-listings",
  "favorites":            "/favorites",
  "my-list":              "/my-list",
  "job-applications":     "/job-applications",
  "locations":            "/locations",
  "advertisements":       "/advertisements",

  // Add / create
  "add-item":             "/add",
  "claim-business":       "/claim-business",

  // Messages
  "message-details":      "/messages/thread",

  // Settings / account
  "settings":             "/settings",
  "change-password":      "/settings/password",
  "payment-methods":      "/settings/payment-methods",
  "date-picker":          "/settings/date-picker",
  "payment":              "/payment",

  // Admin
  "admin-dashboard":      "/admin",
  "user-management":      "/admin/users",
  "user-details":         "/admin/users/details",
  "analytics":            "/admin/analytics",
  "pending-items":        "/admin/pending",
  "flagged-reviews":      "/admin/reviews",
  "flagged-listings":     "/admin/listings",
  "tech-stack":           "/admin/tech-stack",
  "invite-codes":         "/admin/invite-codes",
  "waitlist-admin":       "/admin/waitlist",
  "admin-feedback":       "/admin/feedback",
  "phone-checks":         "/admin/phone-checks",

  // Info / legal
  "about":                "/about",
  "terms":                "/terms",
  "privacy":              "/privacy",
  "feedback":             "/feedback",
  "contacts":             "/contacts",

  // Auth (path-driven screens — pathToTab returns null for these)
  "sign-in":              "/sign-in",
  "verification":         "/verify",

  // Onboarding / tutorial
  "welcome":              "/welcome",
  "onboarding":           "/onboarding",
  "tutorial":             "/tutorial",
  "tutorial-minimal":     "/tutorial/minimal",

  // Other
  "SponsoredAd":          "/sponsored-ad",
  "quick-links":          "/quick-links",
  "qr-code":              "/qr-code",
  "service-success":      "/success",
  "review":               "/review",
  "icon-reference":       "/icons",
  "style-guide":          "/style-guide",
  "ux-style-guide":       "/ux-style-guide",
};

// Build reverse map: path → tab
const PATH_TO_TAB = {};
for (const [tab, path] of Object.entries(TAB_TO_PATH)) {
  PATH_TO_TAB[path] = tab;
}

/**
 * Screens a page load can't land on, mapped to where a refresh should go instead.
 *
 * Two kinds of screen end up here:
 *   - detail views whose subject (which listing, which thread, which user) lives
 *     in React state rather than the URL, so restoring them renders an empty shell
 *   - one-off moments — success, review, add, payment, onboarding — that make no
 *     sense to return to after a reload
 */
const RESTORE_FALLBACK = {
  "item-details": "services",
  "message-details": "messages",
  "user-details": "user-management",
  "service-success": "services",
  "review": "services",
  "add-item": "services",
  "claim-business": "profile",
  "payment": "profile",
  "date-picker": "settings",
  "SponsoredAd": "sponsored-listings",
  "welcome": "services",
  "onboarding": "services",
  "tutorial": "services",
  "tutorial-minimal": "services",
};

export function tabToPath(tab) {
  return TAB_TO_PATH[tab] || "/";
}

export function pathToTab(path) {
  // Exact match first
  if (PATH_TO_TAB[path]) return PATH_TO_TAB[path];

  // Auth routes are handled separately — don't map them to a tab
  if (
    ["/sign-in", "/sign-up", "/forgot-password", "/reset-password", "/verify", "/waitlist"]
      .includes(path)
  ) {
    return null;
  }

  // Fallback: unknown paths default to services
  return "services";
}

/**
 * Which tab a fresh page load at `path` should restore to, substituting a parent
 * screen for anything that can't stand on its own after a reload. Returns null
 * for auth routes, which render straight from the URL.
 */
export function pathToRestoredTab(path) {
  const tab = pathToTab(path);
  if (!tab) return null;
  return RESTORE_FALLBACK[tab] || tab;
}
